import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import React, { useEffect, useState } from "react";
import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderLeft,
  PlaylistOverviewHeaderRight,
  BottomButtons,
  ButtonSection,
  TotalLabel,
  BottomButtonsContainer,
  AddActionButton,
  SearchContainer,
  SearchInputWrapper,
  TotalLabelWrapper,
} from "./GuestCurator.styles";
import { SearchInput } from "@src/components/SearchInput";
import PageTitle from "@src/components/PageTitle";
import CompactIcon from "@src/assets/img/compacts-icon.svg";
import CompactIconActive from "@src/assets/img/compacts-icon-active.svg";
import GuestCuratorList from "@src/components/GuestCuratorList";
import { CreateGuestCurator, IGuestCuratorRecord } from "@src/types/curator";
import { useSettings } from "@src/context/SettingsContext";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import ErrorLabel from "@src/components/ErrorLabel";
import Spinner from "@src/components/Spinner";
import { useNotification } from "@src/context/NotificationContext";
import ContentDrawer from "@src/components/ContentDrawer";
import GuestCuratorForm from "./GuestCuratorForm";
import { useMyCurators } from "@src/hooks/useMyCurators";
import { NoResultsFoundMessage } from "@src/components/NoResultsFound";
import {
  useCreateGuestCuratorMutation,
  useUpdateGuestCuratorMutation,
} from "@src/queries/curatorQueries";
import { useAuthCurator } from "@src/hooks/useAuth";
import { CompactActionButton } from "@src/components/CompactActionButton";
import { IShowRecord } from "@src/types/show";
import { Navigate } from "react-router-dom";

const schema = yup.object().shape({
  searchText: yup
    .string()
    .max(50, "Search text must be less than 50 characters"),
});
type tracksFiltersAppliedType = {
  searchText?: string;
};
const RenderContentStatus = ({
  loading,
  results,
}: {
  loading: boolean;
  results: number;
}): React.JSX.Element | null => {
  if (results) return <></>;
  if (loading) return <Spinner />;
  if (!loading && !results)
    return <NoResultsFoundMessage message="No guest curators found" />;
  return null;
};
const GuestCurator = (): React.JSX.Element => {
  const curator = useAuthCurator();
  if (curator?.isSubcurator) {
    return <Navigate to="/" />;
  }
  const { addNotification } = useNotification();
  const [curatorToEdit, setCuratorToEdit] = useState<CreateGuestCurator | null>(
    null
  );
  const {
    toggleGuestCuratorsTable,
    tableSettings: { guestCuratorsTable },
  } = useSettings();
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] =
    React.useState<boolean>(false);
  const [isUpdateCuratorDrawerOpen, setIsUpdateCuratorDrawerOpen] =
    React.useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] =
    useState<tracksFiltersAppliedType>({
      searchText: "",
    });
  const {
    fetchingMyCurators: fetchingCurators,
    myCurators: curators,
    refetchMyCurators,
  } = useMyCurators(curator?.documentId ?? "", filtersApplied.searchText);
  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchText: "",
    },
  });
  const inputSearchText = watch("searchText");
  const toggleDrawerOpen = ({
    value,
    drawer,
  }: {
    value: boolean;
    drawer: "invite" | "update";
  }): void => {
    switch (drawer) {
      case "invite":
        setIsInviteDrawerOpen(value);
        break;
      case "update":
        setIsUpdateCuratorDrawerOpen(value);
        break;
      default:
        break;
    }
  };
  const [requestingApi, setRequestingApi] = useState(false);
  const disableInputs = fetchingCurators || requestingApi;
  const mutationCreateCurator = useCreateGuestCuratorMutation();
  const mutationUpdateCurator = useUpdateGuestCuratorMutation();
  useEffect(() => {
    const handler = setTimeout(() => {
      setFiltersApplied((prev) => ({
        ...prev,
        searchText: inputSearchText ? inputSearchText.trim() : "",
      }));
    }, 800);
    return (): void => clearTimeout(handler);
  }, [inputSearchText]);

  const submitCreateGuestCurator = async (
    data: CreateGuestCurator
  ): Promise<void> => {
    setRequestingApi(true);
    mutationCreateCurator.mutateAsync(
      {
        name: data.name,
        email: data.email,
        shows: data.shows ?? [],
        about: data.about,
        desktopPhoto: null,
        mobilePhoto: null,
        parentCurator: curator!.documentId,
      },
      {
        onError: () =>
          addNotification("Failed to create guest curator", "error"),
        onSuccess: () => {
          addNotification("Guest curator invite sent successfully", "success");
          toggleDrawerOpen({ value: false, drawer: "invite" });
          refetchMyCurators();
        },
        onSettled: () => setRequestingApi(false),
      }
    );
  };
  const submitUpdateGuestCurator = async (
    data: CreateGuestCurator
  ): Promise<void> => {
    setRequestingApi(true);
    mutationUpdateCurator.mutateAsync(
      {
        name: data.name,
        email: data.email,
        shows: data.shows ?? [],
        about: data.about,
        desktopPhoto: data.desktopPhoto,
        mobilePhoto: data.mobilePhoto,
        showsDropDown: data.showsDropDown,
        id: curatorToEdit?.id,
        documentId: curatorToEdit?.documentId,
      },
      {
        onError: () =>
          addNotification("Failed to update guest curator", "error"),
        onSuccess: () => {
          addNotification("Guest curator updated successfully", "success");
          refetchMyCurators();
          toggleDrawerOpen({ value: false, drawer: "update" });
        },
        onSettled: () => setRequestingApi(false),
      }
    );
  };
  const onClickEdit = async (data: IGuestCuratorRecord): Promise<void> => {
    setCuratorToEdit({
      name: data.name,
      email: data.user.email,
      shows: data.shows.map((show: IShowRecord) => show.documentId),
      about: data.about,
      desktopPhoto: null,
      mobilePhoto: null,
      showsDropDown: data.shows
        .map((show: IShowRecord) => show.documentId)
        .join(","),
      documentId: data.documentId,
      id: data.id,
      desktopPhotoUrl: data.webCuratorCatalog?.url,
      mobilePhotoUrl: data.webCuratorCatalog?.url,
    });
    toggleDrawerOpen({ value: true, drawer: "update" });
  };

  return (
    <WrapperContainer noScroll>
      <PageTopSection>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>Guest Curator</PageTitle>
              <BottomButtonsContainer>
                <BottomButtons>
                  <AddActionButton
                    style={{
                      borderRadius: 50,
                      width: 90,
                    }}
                    $active={true}
                    onClick={() => {
                      toggleDrawerOpen({
                        value: !isInviteDrawerOpen,
                        drawer: "invite",
                      });
                    }}
                  >
                    + Invite
                  </AddActionButton>
                  <CompactActionButton
                    $tooltipContent="Compact view"
                    $active={guestCuratorsTable}
                    onClick={toggleGuestCuratorsTable}
                  >
                    <img
                      src={guestCuratorsTable ? CompactIconActive : CompactIcon}
                      alt="compact"
                      style={{ width: "50%", objectFit: "contain" }}
                    />
                  </CompactActionButton>
                </BottomButtons>
              </BottomButtonsContainer>
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>
          <PlaylistOverviewHeaderRight>
            {fetchingCurators && <Spinner small />}
            <SearchContainer>
              <SearchInputWrapper>
                <ErrorLabel>{errors.searchText?.message}</ErrorLabel>
                <Controller
                  name="searchText"
                  control={control}
                  render={({ field }) => (
                    <SearchInput {...field} placeholder="Search by name" />
                  )}
                />
              </SearchInputWrapper>
              <TotalLabelWrapper>
                <TotalLabel>Total: {curators?.length ?? 0}</TotalLabel>
              </TotalLabelWrapper>
            </SearchContainer>
          </PlaylistOverviewHeaderRight>
        </PlaylistOverviewHeader>
      </PageTopSection>
      <ContentContainer>
        <ContentDrawer
          $floatingDrawer
          isOpen={isInviteDrawerOpen}
          toggleDrawer={() =>
            toggleDrawerOpen({ value: !isInviteDrawerOpen, drawer: "invite" })
          }
        >
          <GuestCuratorForm
            disableInputs={disableInputs}
            isOpen={isInviteDrawerOpen}
            closeButtonHandler={() =>
              toggleDrawerOpen({ value: false, drawer: "invite" })
            }
            fetchingCurators={fetchingCurators}
            submitButtonHandler={submitCreateGuestCurator}
          />
        </ContentDrawer>
        <ContentDrawer
          isOpen={isUpdateCuratorDrawerOpen}
          toggleDrawer={() =>
            toggleDrawerOpen({ value: !isInviteDrawerOpen, drawer: "update" })
          }
        >
          <GuestCuratorForm
            disableInputs={disableInputs}
            isOpen={isUpdateCuratorDrawerOpen}
            initialValues={curatorToEdit || undefined}
            editMode
            closeButtonHandler={() =>
              toggleDrawerOpen({ value: false, drawer: "update" })
            }
            fetchingCurators={fetchingCurators}
            submitButtonHandler={submitUpdateGuestCurator}
          />
        </ContentDrawer>
        <div
          style={{ width: "100%", display: "flex", flexDirection: "column" }}
        >
          <GuestCuratorList onClickEdit={onClickEdit} data={curators} />
          <RenderContentStatus
            loading={fetchingCurators}
            results={curators?.length}
          />
        </div>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default GuestCurator;
